import { Capacitor } from "@capacitor/core";
import { App as CapApp } from "@capacitor/app";
import { StatusBar, Style } from "@capacitor/status-bar";
import { SplashScreen } from "@capacitor/splash-screen";
import { SocialLogin } from "@capgo/capacitor-social-login";

/**
 * Initialize Capacitor native features.
 * Call this once when the React app mounts.
 * No-op on web — all calls are guarded by isNativePlatform().
 */
export async function initCapacitor() {
  if (!Capacitor.isNativePlatform()) return;

  // Status bar: dark background, light text
  try {
    await StatusBar.setStyle({ style: Style.Dark });
    if (Capacitor.getPlatform() === "android") {
      await StatusBar.setBackgroundColor({ color: "#040C18" });
    }
  } catch (e) {
    console.error("[Capacitor] StatusBar error:", e);
  }

  // Hide splash screen after React has mounted
  try {
    await SplashScreen.hide({ fadeOutDuration: 300 });
  } catch (e) {
    console.error("[Capacitor] SplashScreen error:", e);
  }
}

/**
 * Set up Android hardware back button handler.
 * @param {Function} canGoBack - returns true if router has history
 * @param {Function} goBack - navigates back
 */
export function setupBackButton(canGoBack, goBack) {
  if (Capacitor.getPlatform() !== "android") return null;

  const listener = CapApp.addListener("backButton", ({ canGoBack: webCanGoBack }) => {
    if (webCanGoBack || canGoBack()) {
      goBack();
    } else {
      CapApp.exitApp();
    }
  });

  return listener;
}

/**
 * Listen for deep link / URL scheme opens.
 * @param {Function} onUrl - callback with URL string
 * @returns cleanup function
 */
export function onDeepLink(onUrl) {
  if (!Capacitor.isNativePlatform()) return () => {};

  const listener = CapApp.addListener("appUrlOpen", (event) => {
    onUrl(event.url);
  });

  return () => listener.then((h) => h.remove());
}

/**
 * Check if running inside a native Capacitor shell.
 */
export function isNative() {
  return Capacitor.isNativePlatform();
}

let socialLoginReady = false;

/**
 * Native Google Sign-In (no browser, no bridge page). Opens the OS account
 * picker via the Google SDK and returns a Google ID token, which the caller
 * POSTs to /api/v1/auth/google/native for verification + session creation.
 *
 * Requires REACT_APP_GOOGLE_WEB_CLIENT_ID at build time (the Google "Web" OAuth
 * client id — the same value as the server's GOOGLE_CLIENT_ID). iOS additionally
 * needs REACT_APP_GOOGLE_IOS_CLIENT_ID.
 */
export async function nativeGoogleSignIn() {
  const webClientId = process.env.REACT_APP_GOOGLE_WEB_CLIENT_ID;
  if (!webClientId) {
    throw new Error("Google Sign-In is not configured (missing REACT_APP_GOOGLE_WEB_CLIENT_ID).");
  }

  if (!socialLoginReady) {
    const iosClientId = process.env.REACT_APP_GOOGLE_IOS_CLIENT_ID;
    await SocialLogin.initialize({
      google: {
        webClientId,
        ...(iosClientId ? { iOSClientId: iosClientId, iOSServerClientId: webClientId } : {}),
        mode: "online",
      },
    });
    socialLoginReady = true;
  }

  const res = await SocialLogin.login({
    provider: "google",
    options: { scopes: ["email", "profile"] },
  });

  // Android returns the token under result.idToken; other platforms may put it
  // at the top level.
  const idToken =
    (res && res.result && res.result.idToken) || (res && res.idToken) || null;
  if (!idToken) {
    throw new Error("Google Sign-In did not return an ID token.");
  }
  return idToken;
}
